import { Box3, Vector3 } from 'three'
import { ImmutableVector3, getFlatShapeNormal } from '@modugen/ui-toolkit'

// An opening center may sit slightly off the wall's center plane (e.g. layered walls); allow this
// much on top of half the wall thickness.
const PLANE_DISTANCE_TOLERANCE = 0.15
// Used when a wall carries no thickness.
const FALLBACK_WALL_THICKNESS = 0.2
// How far the opening center may lie outside the wall outline (along its axis and in height).
const IN_PLANE_TOLERANCE = 0.05
// Planar walls are vertical; skip degenerate shapes whose normal points mostly up/down.
const MAX_NORMAL_Z = 0.5

interface WallPlane {
  guid: string
  normal: Vector3
  /** Signed distance of the wall plane from the origin (`normal · point`). */
  offset: number
  /** Horizontal in-plane axis of the wall. */
  tangent: Vector3
  tMin: number
  tMax: number
  zMin: number
  zMax: number
  /** Max distance an opening center may have from the plane to count as inside this wall. */
  maxPlaneDistance: number
}

const dot = (a: Vector3, p: { x: number; y: number; z: number }): number =>
  a.x * p.x + a.y * p.y + a.z * p.z

const buildWallPlanes = (walls: ShapeObject[]): WallPlane[] =>
  walls.flatMap(wall => {
    const points = wall.shape.points
    if (points.length < 3) return []

    const flatNormal = getFlatShapeNormal(
      points as [ImmutableVector3, ImmutableVector3, ImmutableVector3, ...ImmutableVector3[]],
    ).normalize()
    // Degenerate (collinear) polygons yield a zero-length normal.
    if (flatNormal.length() < 0.5 || Math.abs(flatNormal.z) > MAX_NORMAL_Z) return []

    const normal = new Vector3(flatNormal.x, flatNormal.y, flatNormal.z)
    const tangent = new Vector3(-normal.y, normal.x, 0).normalize()

    let offsetSum = 0
    let tMin = Infinity
    let tMax = -Infinity
    let zMin = Infinity
    let zMax = -Infinity
    points.forEach(point => {
      offsetSum += dot(normal, point)
      const t = dot(tangent, point)
      tMin = Math.min(tMin, t)
      tMax = Math.max(tMax, t)
      zMin = Math.min(zMin, point.z)
      zMax = Math.max(zMax, point.z)
    })

    return [
      {
        guid: wall.guid,
        normal,
        offset: offsetSum / points.length,
        tangent,
        tMin,
        tMax,
        zMin,
        zMax,
        maxPlaneDistance:
          (wall.thickness ?? FALLBACK_WALL_THICKNESS) / 2 + PLANE_DISTANCE_TOLERANCE,
      },
    ]
  })

/**
 * Infers, for each selected IFC opening, the planar wall it belongs to, purely from geometry: the
 * opening's (aligned world-space) box center must lie on the wall's plane — within half the wall
 * thickness plus a tolerance — and inside the wall outline (axis extent and height). Among several
 * candidate walls (e.g. parallel walls of adjacent rooms) the one whose plane is closest wins.
 * Openings with no matching wall are simply omitted from the result.
 */
export const matchOpeningsToWalls = (
  openingBoxes: Record<string, Box3>,
  walls: ShapeObject[],
): Record<string, string> => {
  const wallPlanes = buildWallPlanes(walls)
  const result: Record<string, string> = {}

  Object.entries(openingBoxes).forEach(([openingGuid, box]) => {
    if (box.isEmpty()) return
    const center = box.getCenter(new Vector3())

    let bestWallGuid: string | null = null
    let bestDistance = Infinity
    wallPlanes.forEach(plane => {
      const planeDistance = Math.abs(dot(plane.normal, center) - plane.offset)
      if (planeDistance > plane.maxPlaneDistance || planeDistance >= bestDistance) return

      const t = dot(plane.tangent, center)
      const inOutline =
        t >= plane.tMin - IN_PLANE_TOLERANCE &&
        t <= plane.tMax + IN_PLANE_TOLERANCE &&
        center.z >= plane.zMin - IN_PLANE_TOLERANCE &&
        center.z <= plane.zMax + IN_PLANE_TOLERANCE
      if (!inOutline) return

      bestWallGuid = plane.guid
      bestDistance = planeDistance
    })

    if (bestWallGuid) result[openingGuid] = bestWallGuid
  })

  return result
}
